import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getUserPayments, downloadInvoicePDF } from '../services/api';
import Alert from '../components/common/Alert';

function BillingHistory() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const data = await getUserPayments();
        // Handle both paginated and non-paginated responses
        const paymentsList = data.results || data;
        setPayments(Array.isArray(paymentsList) ? paymentsList : []);
      } catch (error) {
        console.error('Error fetching payment history:', error);
        setError('Failed to load billing history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const toggleExpanded = (paymentId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(paymentId)) {
      newExpanded.delete(paymentId);
    } else {
      newExpanded.add(paymentId);
    }
    setExpandedItems(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentTypeDisplay = (type) => {
    switch (type?.toLowerCase()) {
      case 'membership':
        return 'Membership';
      case 'event':
        return 'Event Registration';
      case 'donation':
        return 'Donation';
      default:
        return type || 'Payment';
    }
  };

  const handleDownloadInvoice = async (payment) => {
    try {
      await downloadInvoicePDF(payment.id);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setError(error.message || 'Failed to download invoice. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Billing History</h1>
          <p className="text-gray-600 mt-2">
            View and manage your payment history and invoices
          </p>
        </header>

        {/* Error State */}
        {error && (
          <Alert type="error" message={error} onClose={() => setError('')} />
        )}

        {/* Empty State */}
        {!loading && !error && payments.length === 0 && (
          <div className="bg-white shadow rounded-lg p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No billing history</h3>
            <p className="mt-1 text-sm text-gray-500">
              You haven't made any payments yet. Start with a membership or event registration.
            </p>
            <div className="mt-6">
              <Link
                to="/membership"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Choose a Plan
              </Link>
            </div>
          </div>
        )}

        {/* Payment History List */}
        {!loading && !error && payments.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Payment History ({payments.length} {payments.length === 1 ? 'payment' : 'payments'})
              </h2>
            </div>

            <div className="divide-y divide-gray-200">
              {payments.map((payment) => (
                <div key={payment.id} className="hover:bg-gray-50 transition-colors">
                  {/* Main Payment Row */}
                  <div
                    className="px-6 py-4 cursor-pointer"
                    onClick={() => toggleExpanded(payment.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {getPaymentTypeDisplay(payment.payment_type)}
                                {payment.membership_details?.membership_type?.name && (
                                  <span className="text-gray-500 ml-1">
                                    - {payment.membership_details.membership_type.name}
                                  </span>
                                )}
                              </p>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                                {payment.status?.charAt(0).toUpperCase() + payment.status?.slice(1) || 'Unknown'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {formatDate(payment.payment_date || payment.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {formatAmount(payment.amount)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {payment.payment_method || 'Credit Card'}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <svg
                            className={`w-5 h-5 text-gray-400 transform transition-transform ${
                              expandedItems.has(payment.id) ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedItems.has(payment.id) && (
                    <div className="px-6 pb-4 border-t border-gray-100 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                        {/* Payment Details */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Payment Details</h4>
                          <dl className="space-y-2">
                            <div>
                              <dt className="text-xs text-gray-500">Transaction ID</dt>
                              <dd className="text-sm text-gray-900 font-mono" title={payment.transaction_id}>
                                {payment.transaction_id
                                  ? `${payment.transaction_id.substring(0, 8)}...${payment.transaction_id.slice(-4)}`
                                  : 'N/A'
                                }
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Payment Method</dt>
                              <dd className="text-sm text-gray-900">
                                {payment.payment_method || 'Credit Card'}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Type</dt>
                              <dd className="text-sm text-gray-900">
                                {getPaymentTypeDisplay(payment.payment_type)}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500">Auto Renewal</dt>
                              <dd className="text-sm text-gray-900">
                                {payment.membership_details ? (
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    payment.membership_details.auto_renew
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {payment.membership_details.auto_renew ? 'Enabled' : 'Disabled'}
                                  </span>
                                ) : 'N/A'}
                              </dd>
                            </div>
                            {payment.failure_reason && (
                              <div>
                                <dt className="text-xs text-gray-500">Failure Reason</dt>
                                <dd className="text-sm text-red-600">{payment.failure_reason}</dd>
                              </div>
                            )}
                          </dl>
                        </div>

                        {/* Membership/Service Details */}
                        {payment.membership_details && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Membership Details</h4>
                            <dl className="space-y-2">
                              <div>
                                <dt className="text-xs text-gray-500">Plan</dt>
                                <dd className="text-sm text-gray-900">
                                  {payment.membership_details.membership_type?.name || 'N/A'}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-xs text-gray-500">Period Start</dt>
                                <dd className="text-sm text-gray-900">
                                  {payment.membership_details.current_period_start
                                    ? new Date(payment.membership_details.current_period_start + 'T00:00:00').toLocaleDateString()
                                    : 'N/A'}
                                </dd>
                              </div>
                              {payment.membership_details.current_period_end && (
                                <div>
                                  <dt className="text-xs text-gray-500">Period End</dt>
                                  <dd className="text-sm text-gray-900">
                                    {new Date(payment.membership_details.current_period_end + 'T00:00:00').toLocaleDateString()}
                                  </dd>
                                </div>
                              )}
                              <div>
                                <dt className="text-xs text-gray-500">Duration</dt>
                                <dd className="text-sm text-gray-900">
                                  {payment.membership_details.membership_type?.duration_months
                                    ? `${payment.membership_details.membership_type.duration_months} months`
                                    : 'N/A'}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        )}

                        {/* Actions */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Actions</h4>
                          <div className="space-y-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadInvoice(payment);
                              }}
                              className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              disabled={payment.status !== 'completed'}
                            >
                              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Download Invoice
                            </button>
                            {payment.status === 'completed' && payment.stripe_receipt_url && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(payment.stripe_receipt_url, '_blank');
                                }}
                                className="w-full inline-flex items-center justify-center px-3 py-2 border border-blue-300 shadow-sm text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                View Receipt
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BillingHistory;